import { createTimeInstance } from '@utils/time';

// Centralized human labels + formatters for the Score Backfill feature. Every surface that
// shows a status, scope, index code, spec id, count, cost, or date routes through here so the
// same machine value never renders two different ways across screens (see #1 in the UX plan).

const EM_DASH = '—';

// Title-case an unknown snake_case / spaced token so we never echo raw machine strings.
const humanizeToken = (value) =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

// `canceling` is a display-only status — the API never sends it. See deriveDisplayStatus.
export const STATUS_LABELS = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
  canceling: 'Canceling…',
};

// A cancel is cooperative: the job carries cancel_requested_at while status is still `running`
// until the worker finalizes it. Every surface routes its status through here so that window
// renders as "Canceling…" rather than a still-Running badge. Deliberately matched against the
// non-terminal statuses (see BACKFILL_TERMINAL_STATUSES) so a cancel that lost the race to a
// finishing run doesn't relabel an already-completed job — and so this stays a leaf display
// module with no dependency on the service layer.
const CANCELABLE_STATUSES = ['queued', 'running'];

export const deriveDisplayStatus = (job) => {
  if (!job) return null;
  if (job.cancel_requested_at && CANCELABLE_STATUSES.includes(job.status)) return 'canceling';
  return job.status;
};

export const SCOPE_LABELS = {
  full_history: 'Full history',
  latest_per_company: 'Latest per company',
};

export const UNIVERSE_KIND_LABELS = {
  whole_corpus: 'Whole corpus',
  index: 'Index',
  tickers: 'Tickers',
};

// Best-effort pretty names for known index codes; unknown codes fall back to upper-case.
export const INDEX_LABELS = {
  sp500: 'S&P 500',
  sp400: 'S&P 400',
  sp600: 'S&P 600',
  sp1500: 'S&P 1500',
  russell1000: 'Russell 1000',
  russell2000: 'Russell 2000',
  russell3000: 'Russell 3000',
  nasdaq100: 'Nasdaq 100',
  dow30: 'Dow 30',
};

export const formatStatus = (status) => STATUS_LABELS[status] || (status ? humanizeToken(status) : EM_DASH);

export const formatScope = (scope) => SCOPE_LABELS[scope] || (scope ? humanizeToken(scope) : EM_DASH);

export const formatUniverseKind = (kind) => UNIVERSE_KIND_LABELS[kind] || (kind ? humanizeToken(kind) : EM_DASH);

export const formatIndex = (code) => {
  if (!code) return EM_DASH;
  const key = String(code).toLowerCase();
  return INDEX_LABELS[key] || String(code).toUpperCase();
};

// 'forward_dividend_likelihood_v1' -> 'Forward Dividend Likelihood (v1)'
export const formatSpecId = (id) => {
  if (!id) return EM_DASH;
  const parts = String(id)
    .split(/[_\s]+/)
    .filter(Boolean);
  const rendered = parts.map((part) => {
    if (/^v\d+$/i.test(part)) return `(${part.toLowerCase()})`;
    return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
  });
  return rendered.join(' ').replace(/\s+\(/g, ' (');
};

export const formatCount = (n) => (typeof n === 'number' && isFinite(n) ? n.toLocaleString() : EM_DASH);

export const formatUsd = (n) =>
  typeof n === 'number' && isFinite(n)
    ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : EM_DASH;

export const formatDate = (d) => (d ? createTimeInstance(d).format('MMM D, YYYY') : EM_DASH);

export const formatWindow = (from, to) => `${formatDate(from)} → ${formatDate(to)}`;

// Since ai_copilot#592 (prod 2026-09-30) a backfill's from/to bound each occasion's utc_knowable_at
// — its trigger time, read as UTC days, for every spec kind: the same column the feed's knowable
// window bounds. Before the switch the same two dates bounded release dates (calendar specs) or the
// trigger document's publish date. The request and the stored fields never changed, only their
// meaning, so the API says which on `window_bounds`: 'utc_knowable_at', or null for a job created
// before the switch. Only an explicit null is a legacy window — a job with no `window_bounds` key
// at all came from an API that predates the field and says nothing either way.
export const isLegacyWindow = (job) => !!job && job.window_bounds === null;

// The two phrasings every surface that shows a legacy job's dates draws from, so History and
// Progress cannot describe the same run two ways. The short one fits a table cell; the long one says
// what the dates bounded, and why the same dates on a new run would select differently.
export const LEGACY_WINDOW_LABEL = 'Document dates';
export const LEGACY_WINDOW_NOTE =
  'This run’s window bounded document dates. Runs since Sep 30, 2026 bound when occasions became knowable, so the same dates may select different occasions.';

// Since ai_copilot#700 / nvstr-webapp#539 a backfill can run with force_knowable_at_to_scoring_time:
// each company's latest occasion knowable between `from` and the moment the run was requested, every
// score stamped at scoring time rather than at the occasion. It is required for a spec that enables a
// present-day context setting (web/social, fundamentals, estimates, holdings, price returns, FYE,
// abbreviations), whose context is real only as of the moment it is fetched.
//
// On the job row the flag is a plain boolean, false on every job that predates it. Only an explicit
// true reads as stamped: an absent key is an older API that never stamped anything. A positive claim,
// read the way marketCapVersionOf reads its field below — not the way isLegacyWindow reads a negative one.
export const isStampedAtScoringTime = (job) => !!job && job.force_knowable_at_to_scoring_time === true;

// The picker entry's `backfill_allowed` is false when the spec enables a present-day setting, so a
// backfill of it MUST be stamped. Only an explicit false requires it — isLegacyWindow's rule, for the
// same reason: a missing key is a server that predates the field and says nothing, and a 400 from such
// a server names the flag verbatim. Reading absence as a requirement would stamp every spec on an
// older backend.
export const specRequiresStamp = (spec) => !!spec && spec.backfill_allowed === false;

export const formatDateTime = (d) => (d ? createTimeInstance(d).format('MMM D, YYYY h:mm A') : EM_DASH);

// A job's window as History and Progress show it. A stamped job has no occasion_to — its window ended
// at the moment it was requested — and the job row does not carry fapi's exact instant (knowable_to is
// on the create response only), so the request date stands in: created_at, written a moment before
// fapi read its clock. The two differ only across a UTC midnight inside that moment.
export const formatJobWindow = (job) => {
  if (!job) return formatWindow(null, null);
  const to = isStampedAtScoringTime(job) && job.occasion_to == null ? job.created_at : job.occasion_to;
  return formatWindow(job.occasion_from, to);
};

// The two phrasings for a stamped job, as LEGACY_WINDOW_LABEL / LEGACY_WINDOW_NOTE above: a short one
// for a table cell and a recap, a long one that says what the stamp means and how to publish after it.
export const STAMPED_AT_SCORING_TIME_LABEL = 'Stamped at scoring time';
export const STAMPED_AT_SCORING_TIME_NOTE =
  'This run scored each company’s latest occasion knowable when it was requested, and stamped each score at scoring time rather than at the occasion. Publish this spec only over windows that start at or after this run.';

export const formatScored = (completed, total) => `${formatCount(completed)} / ${formatCount(total)}`;

// Prefer an explicit human name; otherwise render the score_spec_id as a clean title
// (e.g. "forward_buyback_intent_5_mini_v1" -> "Forward Buyback Intent 5 Mini (v1)"). The
// description is intentionally not used as the label — it's sentence-length prose, not a name.
export const specDisplayName = (spec) => {
  if (!spec) return EM_DASH;
  return spec.name || formatSpecId(spec.score_spec_id);
};

export const specLabelById = (specs, specId) => {
  const match = (specs || []).find((s) => s.score_spec_id === specId);
  return match ? specDisplayName(match) : formatSpecId(specId);
};

// Since nvstr-webapp#523 / ai_copilot#598 a backfill records the market-cap band its universe was
// drawn from: 'v1' or 'v2'. A job created before that change carries market_cap_version: null, and
// every one of those ran on v1 — so null reads as v1.
//
// Deliberately NOT isLegacyWindow's "only an explicit null counts" rule above, and the reason is the
// sign of the field. window_bounds' null is NEGATIVE information ("these dates did not mean what
// today's mean"), so an absent key must not be read as that claim — a warning may never fire on a
// guess. market_cap_version's null is POSITIVE information ("v1"), and an absent key implies the
// same v1 by a second, independent route: a payload with no such key comes from a deploy where v2
// did not exist, so nothing it describes could have run on v2. Both absences resolve to one fact,
// leaving no distinction to draw. Do not "fix" this to `=== null`.
export const DEFAULT_MARKET_CAP_VERSION = 'v1';

export const marketCapVersionOf = (job) => {
  const raw = job?.market_cap_version;
  if (typeof raw !== 'string' || !raw.trim()) return DEFAULT_MARKET_CAP_VERSION;
  return raw.trim().toLowerCase();
};

// Always self-describing — never a bare "v1". Spec ids render their own version suffix (formatSpecId:
// "Forward Dividend Likelihood (v1)"), so a bare version one separator away would be two different
// versions in one sentence. Takes the VERSION, not the job: the picker renders values that belong to
// no job. For a job, compose — formatMarketCapVersion(marketCapVersionOf(job)). A version this client
// doesn't recognize is title-cased like any other unknown token, so a raw machine string never
// reaches the screen.
export const formatMarketCapVersion = (version) => {
  const v = typeof version === 'string' && version.trim() ? version.trim().toLowerCase() : DEFAULT_MARKET_CAP_VERSION;
  return `Market cap ${/^v\d+$/.test(v) ? v : humanizeToken(v)}`;
};

// The versions fapi has LOADED, from the specs payload's universe_options. Today ['v1']; ['v1','v2']
// once v2's initial load lands. Never hard-coded — "v2 exists" and "v2 is loaded" are different facts
// and only the server knows the second one. A Rails predating #523 sends no key at all, which yields
// [] — that is what hides the picker and keeps the field off the wire on an old backend.
export const offeredMarketCapVersions = (universeOptions) => {
  const list = universeOptions?.market_cap_versions;
  if (!Array.isArray(list)) return [];
  return list.filter((v) => typeof v === 'string' && v.trim()).map((v) => v.trim());
};

// The form's value, clamped to what the server currently offers. null is a real value here and means
// "the analyst did not choose": the request omits the field, and omission is documented as unchanged
// behavior, which is v1. So a candidate the server no longer offers falls back to null (omit) rather
// than to a guess — omitting is the one request that can never 422.
//
// Used by the page's reconcile effect AND by startSimilar. startSimilar needs its own call because
// that effect keys on universeOptions, which does not change on a re-seed — the same gap the
// wholeCorpusDisallowed clamp in startSimilar already patches by hand.
export const resolveMarketCapVersion = (versions, candidate) =>
  candidate && (versions || []).includes(candidate) ? candidate : null;

// Comparing bands is a re_export, never a second create: fapi's already-scored check ignores the
// version, so a second run over a window already scored covers only the companies the other band
// adds — a partial run that looks like a full one. The only control on the form that can cause that
// mistake is the picker, so the correction lives under it.
export const MARKET_CAP_COMPARE_NOTE =
  'A second run over a window already scored only covers companies the other band adds. To compare bands, re-publish an existing run from its job page.';
